// 工具頁 (雷達) — one page per tool at /owner-board/tools/:key, the shape Magnus asked for on
// 2026-10-16 ("make more of the pages like 爆款雷達 … I just select the ones … as an
// interactive tool"): real items with a photo, the numbers and Claude's call on each; he ticks
// the ones to act on and one button starts the run (POST /api/owner-board/radars/:key/run).
// The workspace scripts/oms/*_radar.py write the snapshot (radar_common.py documents it), so a
// new tool needs no change here. A run leaves a board card and opens its conversation.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Paper, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import api from '../services/api';
import { errMessage } from '../components/agent/agentShared';

type Tone = 'good' | 'warn' | 'bad' | 'neutral';

interface RadarItem {
  id: string;
  img?: string | null;
  title: string;
  sub?: string;
  metrics?: { l: string; v: string; tone?: Tone }[];
  call?: string;
  why?: string;
  default_selected?: boolean;
  /** An OMS page for this item, e.g. the order's card on 收貨 (/receiving?order=…). */
  link?: { href: string; label: string };
}

interface RadarSection {
  key: string;
  title: string;
  tone?: Tone;
  note?: string;
  items: RadarItem[];
}

interface RadarAction {
  key: string;
  label: string;
  sections: string[];
  max?: number;
  /** true when the run has a step that reaches people (customer messages): it still asks first. */
  outward?: boolean;
}

interface Radar {
  key: string;
  title: string;
  subtitle?: string;
  headline?: string;
  generated_at?: string;
  tiles?: { v: string; l: string; tone?: Tone }[];
  sections: RadarSection[];
  actions: RadarAction[];
  guide?: string;
  // 'lg' = bigger photos for radars where the picture IS the decision; 'gallery' = photo-card
  // grid (新款雷達: Magnus 10-22 "the images are too small" at 96px).
  thumb?: 'lg' | 'gallery';
}

const TONE_FG: Record<Tone, string> = { good: '#1d7a45', warn: '#9a6200', bad: '#b3261e', neutral: 'inherit' };
const TONE_BG: Record<Tone, string> = { good: '#e5f3ea', warn: '#fbf1dc', bad: '#fbe9e7', neutral: '#eef0f2' };

const asOf = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(5, 16).replace('T', ' ')
    : d.toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const RadarToolPage: React.FC = () => {
  const { toolKey } = useParams<{ toolKey: string }>();
  const navigate = useNavigate();
  const [radar, setRadar] = useState<Radar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openSec, setOpenSec] = useState<Record<string, boolean>>({});
  const [confirm, setConfirm] = useState<RadarAction | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/owner-board/radars/${toolKey}`);
      const raw = res.data.radar || {};
      // A snapshot missing sections/actions must not crash the page.
      const r: Radar = {
        ...raw,
        sections: (raw.sections ?? []).map((s: RadarSection) => ({ ...s, items: s.items ?? [] })),
        actions: (raw.actions ?? []).map((a: RadarAction) => ({ ...a, sections: a.sections ?? [] })),
      };
      setRadar(r);
      setSelected(new Set(r.sections.flatMap((s) => s.items.filter((it) => it.default_selected).map((it) => it.id))));
      // Sections with something to act on start open; 繼續等-type sections start closed.
      setOpenSec(Object.fromEntries(r.sections.map((s) => [s.key, r.actions.some((a) => a.sections.includes(s.key))])));
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [toolKey]);

  useEffect(() => {
    load();
  }, [load]);

  const sectionOf = useMemo(() => {
    const m: Record<string, string> = {};
    radar?.sections.forEach((s) => s.items.forEach((it) => (m[it.id] = s.key)));
    return m;
  }, [radar]);

  // Each action counts only the ticked items in its own sections.
  const forAction = (a: RadarAction) => Array.from(selected).filter((id) => a.sections.includes(sectionOf[id]));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSection = (sec: RadarSection, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      sec.items.forEach((it) => (on ? next.add(it.id) : next.delete(it.id)));
      return next;
    });

  const run = async (a: RadarAction) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/owner-board/radars/${toolKey}/run`, { action: a.key, ids: forAction(a) });
      navigate(`/owner-board/t/${res.data.thread_id}`);
    } catch (err) {
      setError(errMessage(err));
      setBusy(false);
      setConfirm(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!radar) {
    return (
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/owner-board')}>
          回每日看板
        </Button>
        <Alert severity="info" sx={{ mt: 2 }}>
          {error || '這個工具還沒有資料。'}
        </Alert>
      </Box>
    );
  }

  const actionable = radar.actions.filter((a) => forAction(a).length > 0);
  const thumbW = radar.thumb === 'lg' ? 96 : 54;
  // 'lg' shipped first (10-21) and is upgraded to the gallery: the only radar using it is 新款雷達.
  const gallery = radar.thumb === 'gallery' || radar.thumb === 'lg';
  const maxOf = (a: RadarAction) => (Number.isInteger(a.max) ? (a.max as number) : 40);

  return (
    <Box sx={{ maxWidth: 820, width: '100%', mx: 'auto', minWidth: 0, pb: 14, display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/owner-board')} sx={{ px: 0.5 }}>
          每日看板
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          資料 {asOf(radar.generated_at)}
        </Typography>
      </Stack>

      <Box>
        <Typography component="h1" sx={{ fontFamily: '"Noto Serif TC", Georgia, serif', fontWeight: 700, fontSize: { xs: 24, md: 28 } }}>
          {radar.title}
        </Typography>
        {radar.subtitle && (
          <Typography variant="body2" color="text.secondary">
            {radar.subtitle}
          </Typography>
        )}
      </Box>

      {!!radar.tiles?.length && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: `repeat(${Math.min(radar.tiles.length, radar.tiles.length > 3 ? 2 : 3)}, minmax(0,1fr))`, sm: `repeat(${Math.min(radar.tiles.length, 4)}, minmax(0,1fr))` }, gap: 1 }}>
          {radar.tiles.map((t, i) => (
            <Paper key={`${i}-${t.l}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2, minWidth: 0 }}>
              <Typography sx={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontSize: { xs: 20, md: 24 }, color: TONE_FG[t.tone || 'neutral'], fontVariantNumeric: 'tabular-nums' }}>
                {t.v}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                {t.l}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {radar.sections.map((sec) => {
        const tone = sec.tone || 'neutral';
        const pickable = radar.actions.some((a) => a.sections.includes(sec.key));
        const nSel = sec.items.filter((it) => selected.has(it.id)).length;
        const isOpen = openSec[sec.key] ?? true;
        return (
          <Box key={sec.key}>
            <Stack direction="row" alignItems="center" spacing={1} onClick={() => setOpenSec((p) => ({ ...p, [sec.key]: !isOpen }))} sx={{ cursor: 'pointer', mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{sec.title}</Typography>
              <Chip size="small" label={sec.items.length} sx={{ bgcolor: TONE_BG[tone], color: TONE_FG[tone], fontWeight: 700 }} />
              {pickable && isOpen && (
                <Button
                  size="small"
                  sx={{ ml: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection(sec, nSel < sec.items.length);
                  }}
                >
                  {nSel < sec.items.length ? '全選' : '全不選'}
                </Button>
              )}
              {!isOpen && (
                <Typography variant="caption" color="primary" sx={{ ml: 'auto' }}>
                  展開
                </Typography>
              )}
            </Stack>
            {sec.note && isOpen && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                {sec.note}
              </Typography>
            )}
            <Collapse in={isOpen} unmountOnExit>
              {gallery ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(3, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }, gap: 1.25 }}>
                  {sec.items.map((it) => {
                    const on = selected.has(it.id);
                    return (
                      <Paper
                        key={it.id}
                        variant="outlined"
                        onClick={pickable ? () => toggle(it.id) : undefined}
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: pickable ? 'pointer' : 'default',
                          borderColor: on ? TONE_FG.good : 'divider',
                          borderWidth: on ? 2 : 1,
                          bgcolor: on ? '#eef5f2' : 'background.paper',
                        }}
                      >
                        <Box sx={{ position: 'relative', aspectRatio: '3 / 4', bgcolor: '#eceef1' }}>
                          {it.img && (
                            <Box
                              component="img"
                              src={it.img}
                              alt=""
                              loading="lazy"
                              onError={(e: React.SyntheticEvent<HTMLImageElement>) => (e.currentTarget.style.display = 'none')}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          )}
                          {pickable && (
                            <Checkbox
                              checked={on}
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggle(it.id)}
                              sx={{ position: 'absolute', top: 4, left: 4, p: 0.25, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1, '&:hover': { bgcolor: '#fff' } }}
                            />
                          )}
                          {it.call && (
                            <Chip size="small" label={it.call} sx={{ position: 'absolute', top: 6, right: 6, height: 22, bgcolor: TONE_BG[tone], color: TONE_FG[tone], fontWeight: 700 }} />
                          )}
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {it.title}
                          </Typography>
                          {it.sub && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {it.sub}
                            </Typography>
                          )}
                          {it.why && (
                            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35, mt: 0.25 }}>
                              {it.why}
                            </Typography>
                          )}
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                            {it.link ? (
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const href = it.link!.href;
                                  if (/^https?:\/\//.test(href)) window.open(href, '_blank', 'noopener');
                                  else navigate(href);
                                }}
                                sx={{ px: 0, minWidth: 0, fontSize: 12.5 }}
                              >
                                {it.link.label} →
                              </Button>
                            ) : <span />}
                            {(it.metrics || []).slice(0, 1).map((m, i) => (
                              <Typography key={i} sx={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontSize: 13, fontWeight: 500, color: TONE_FG[m.tone || 'neutral'] }}>
                                {m.v}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {sec.items.map((it) => {
                  const on = selected.has(it.id);
                  return (
                    <Box
                      key={it.id}
                      onClick={pickable ? () => toggle(it.id) : undefined}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: pickable ? `34px ${thumbW}px minmax(0,1fr) auto` : `${thumbW}px minmax(0,1fr) auto`,
                        gap: 1,
                        alignItems: 'center',
                        px: 1.25,
                        py: 1,
                        borderTop: 1,
                        borderColor: 'divider',
                        '&:first-of-type': { borderTop: 0 },
                        cursor: pickable ? 'pointer' : 'default',
                        bgcolor: on ? '#eef5f2' : 'transparent',
                      }}
                    >
                      {pickable && <Checkbox checked={on} size="small" sx={{ p: 0.5 }} onClick={(e) => e.stopPropagation()} onChange={() => toggle(it.id)} />}
                      <Box sx={{ width: thumbW, height: thumbW * 4 / 3, borderRadius: 1, bgcolor: '#eceef1', overflow: 'hidden' }}>
                        {it.img && (
                          <Box
                            component="img"
                            src={it.img}
                            alt=""
                            loading="lazy"
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => (e.currentTarget.style.display = 'none')}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {it.title}
                        </Typography>
                        {it.sub && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {it.sub}
                          </Typography>
                        )}
                        {it.why && (
                          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                            {it.why}
                          </Typography>
                        )}
                        {it.link && (
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const href = it.link!.href;
                              // Shop pages on other sites open in a new tab; OMS paths stay in-app.
                              if (/^https?:\/\//.test(href)) window.open(href, '_blank', 'noopener');
                              else navigate(href);
                            }}
                            sx={{ px: 0, minWidth: 0, mt: 0.25, fontSize: 12.5 }}
                          >
                            {it.link.label} →
                          </Button>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right', maxWidth: 104 }}>
                        {(it.metrics || []).map((m, i) => (
                          <Typography key={i} sx={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontSize: 14, fontWeight: 500, color: TONE_FG[m.tone || 'neutral'] }}>
                            {m.v}
                          </Typography>
                        ))}
                        {it.call && <Chip size="small" label={it.call} sx={{ mt: 0.5, bgcolor: TONE_BG[tone], color: TONE_FG[tone], fontWeight: 700 }} />}
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
              )}
            </Collapse>
          </Box>
        );
      })}

      {radar.guide && (
        <Typography variant="caption" color="text.secondary">
          {radar.guide}
        </Typography>
      )}

      {/* ── action bar: one button per action that has ticked items ── */}
      {actionable.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'sticky',
            bottom: { xs: 8, md: 16 },
            zIndex: 6,
            borderRadius: 3,
            p: 1.25,
            display: 'grid',
            gap: 1,
          }}
        >
          {confirm ? (
            <>
              {forAction(confirm).length > maxOf(confirm) && (
                <Typography variant="body2" color="error">
                  一次最多 {maxOf(confirm)} 項，請先取消勾選一些。
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {confirm.label}：{forAction(confirm).length} 項？Claude 會照做
                {confirm.outward ? '；要傳給客人的訊息會先給你看，你核准才傳。' : '，做完讀回給你看。'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disableElevation disabled={busy || forAction(confirm).length > maxOf(confirm)} onClick={() => run(confirm)} sx={{ flex: 1, bgcolor: '#1f5b4c', '&:hover': { bgcolor: '#174538' } }}>
                  {busy ? '送出中…' : '確定，開始做'}
                </Button>
                <Button color="inherit" disabled={busy} onClick={() => setConfirm(null)}>
                  取消
                </Button>
              </Stack>
            </>
          ) : (
            <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {actionable.map((a, i) => (
                <Button
                  key={a.key}
                  variant={i === 0 ? 'contained' : 'outlined'}
                  disableElevation
                  onClick={() => setConfirm(a)}
                  sx={{ flex: '1 1 160px', ...(i === 0 ? { bgcolor: '#1f5b4c', '&:hover': { bgcolor: '#174538' } } : {}) }}
                >
                  {a.label}（{forAction(a).length}{forAction(a).length > maxOf(a) ? ` · 最多 ${maxOf(a)}` : ''}）
                </Button>
              ))}
            </Stack>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RadarToolPage;
